import Resource from "../base/resource";

class Model extends Resource {
    name;
    id;

    constructor(props) {
        super(props);
        this.name = window.noserver ? props.name : this.getModelName(props.resourceId);
        this.id = props.resourceId;
        this.version = props.version;
    }

    async load(onLoadCallback) {
        if (wv.effectController.stoppedTrainingId === TrainingTreeController._trainingFeature.getActiveTraining()?.id) {
            return;
        }

        const loadModel = async (serverLess) => {
            if (serverLess) {
                if (cacheFile?.core?.modeller === 'potree' || cacheFile?.core?.modeller === 'e57') {
                    await model_load_helper.loadPointCloudModel(this.name, this.id, this.resourceId, this.version);
                } else if (cacheFile?.core?.modeller === GaussianSplatsManager.GaussianSplatType) {
                    await model_load_helper.loadGaussianSplatModel(this.name, this.id, this.resourceId, this.version);
                } else {
                    await model_load_helper.loadCadModel(this.name, this.id, this.resourceId, this.version);
                }
            } else {
                model_load_helper.sendLoadOrAppendMessage(text_loadModel, "STEP", this.id, true, true, this.version);
            }
        };

        console.log('Loading model: ', this.name);
        if (getModelObject(this.id)) {
            const model = modelManager.getModelById(this.id);
            if (model && model.version === this.version) {
                onLoadCallback(1);

                const animationDuration = wv.effectController.serverlessEditorLoadingState ? 0 : undefined;
                processVisibilityMessage(undefined, this.id, SelectionActionApplyToType.Model, false, false, undefined, animationDuration);
                await GroupController.loadGroupsOnModelLoad(model.id);

                return;
            }

            removeModelFromScene(this.id);
        }

        if (!wv.effectController.meetingInfo.data.cacheFileDetails.find(cache => cache._id === this.resourceId)) {
            await wv.availableFileList.updateAvailableFiles([this.resourceId]);
        }

        const cacheFile = wv.effectController.meetingInfo.data.cacheFileDetails.find(cache => cache._id === this.resourceId);

        await loadModel(window.noserver);

        for (; ;) {
            if (!getModelObject(this.id)) {
                await sleep(1000);
                continue;
            }

            onLoadCallback(1);

            if (wv.effectController.serverlessEditorLoadingState) {
                return;
            }

            const model = modelManager.getModelById(this.id);

            processVisibilityMessage(undefined, this.id, SelectionActionApplyToType.Model, false, false);

            return;
        }
    }

    setState(state, {patchState, appendState, staticPatchState}) {
        explodeModel(state.explosion, this.id);
        changePerformMoveOnGroup(true);
        this._setModelConfiguration(state.visible, state.position, state.quaternion, state.scale, appendState, state);
        this._setModelSelection(state.highlight, state.visibility, state.transparency, !(patchState || appendState || staticPatchState));
        if (!patchState && !appendState && !staticPatchState) {
            this._resetMovedVolumes();
            this._resetMovedBranches();
        }

        this._setBranchConfiguration(state.branches, appendState);
        this._setVolumeConfiguration(state.volumes, appendState);
        this._setGroupConfiguration(state.groups, appendState);
        changePerformMoveOnGroup(false);
        this._processGlbAnimation(state.glbAnimation, state.animationLoop, !state.patchState);
    }


    _setModelSelection(highlightData, visibilityData, transparencyData, resetExistingSelection) {
        if (resetExistingSelection) {
            this.resetHighlightAndTransparency(transparencyData);
        }
        performHighlight(highlightData);
        performVisibility(visibilityData, 0);
        performTransparency(transparencyData, 0);
    }

    resetHighlightAndTransparency(transparencyData = {}) {
        const model = getModelObject(this.id);
        const modelType = modelManager.getModelType(this.id);
        if (!model) {
            return;
        }
        resetHighlightOfThisModel(model, modelType);
        wv.resetTransparencyModel(model.modelId, 0, transparencyData);
    }

    _setModelConfiguration(isVisible, position, quaternion, scale, appendState, stateObject = {}) {
        const model = getModelObject(this.id);
        CADModelTransformationAnimationManager.completeAllOfThisModel(this.id);
        if (!model) {
            throw new Error('Can not get model object for ' + this.name);
        }

        if (appendState) {
            let finalMatrix = model.matrix.clone();
            position = new THREE.Vector3(...position);
            quaternion = new THREE.Quaternion(...quaternion);
            scale = new THREE.Vector3(...scale);
            finalMatrix.multiply(new THREE.Matrix4().compose(position, quaternion, scale));
            finalMatrix.decompose(position, quaternion, scale);
            position = position.toArray();
            quaternion = quaternion.toArray();
            scale = scale.toArray();
        }

        if (position) model.position.set(...position);
        if (quaternion) model.quaternion.set(...quaternion);
        if (scale) model.scale.set(...scale);
        if (!isNullOrUndefined(isVisible)) {
            processVisibilityMessage(undefined, this.id, SelectionActionApplyToType.Model, false, isVisible, stateObject, 0);
        }

    }

    _resetMovedBranches() {
        let allMovedBranch = Object.keys(moved_branch_matrices);
        allMovedBranch = allMovedBranch.filter(branchId => modelManager.getModelIdByBranchId(branchId) === this.id);

        allMovedBranch.forEach(branchId => {
            resetThisVolume(branchId, false);
            delete moved_branch_matrices[branchId];
        });
    }

    _resetMovedVolumes() {
        let allMovedVolumes = Object.keys(moved_volume_matrices);
        allMovedVolumes = allMovedVolumes.filter(branchId => modelManager.getModelIdByBranchId(branchId) === this.id);

        allMovedVolumes.forEach(branchId => {
            resetThisVolume(branchId, false);
            delete moved_volume_matrices[branchId];
        });
    }

    _setVolumeConfiguration(volumes, appendState) {
        if (isNullOrUndefined(volumes)) {
            return;
        }

        volumes.forEach(volume => {
            const {position, quaternion, scale} = this._getMoveConfiguration(volume);
            const volumeFinalMatrix = new THREE.Matrix4().compose(position, quaternion, scale);
            volume.branchID = volume.branchID || volume.name;
            moveVolume(volume, volumeFinalMatrix, false, 2000, !!appendState);
        });
    }

    _setBranchConfiguration(branches, appendState) {
        if (isNullOrUndefined(branches)) {
            return;
        }

        branches.forEach(branch => {
            const {position, quaternion, scale} = this._getMoveConfiguration(branch);
            const volumeFinalMatrix = new THREE.Matrix4().compose(position, quaternion, scale);
            moveBranch(branch.branchId, volumeFinalMatrix, false, 2000, !!appendState);
        });
    }

    _setGroupConfiguration(groups, appendState) {
        if (isNullOrUndefined(groups)) {
            return;
        }

        groups.forEach(group => {
            const {position, quaternion, scale} = this._getMoveConfiguration(group);
            const volumeFinalMatrix = new THREE.Matrix4().compose(position, quaternion, scale);
            moveGroup(group.groupName, volumeFinalMatrix, false, 2000, !!appendState);
        });
    }

    _getMoveConfiguration(props) {
        const {position, quaternion, scale} = Object.assign({
            position: [0, 0, 0],
            quaternion: [0, 0, 0, 1],
            scale: [1, 1, 1],
        }, props);
        return {
            position: new THREE.Vector3().set(...position),
            quaternion: new THREE.Quaternion().set(...quaternion),
            scale: new THREE.Vector3().set(...scale)
        }
    }

    _processGlbAnimation(animationName, animationLoop, stopCurrentAnimations) {
        const model = modelManager.getModelById(this.id);
        if (!model) return;
        if (stopCurrentAnimations) modelManager.stopAnimation(model.id);
        if (!animationName) {
            return;
        }

        modelManager.playAnimation(model.id, animationName, animationLoop);
    }

    mergeStates(initialState) {
        if (this.states.length === 0) {
            return initialState;
        }

        let finalState = initialState;
        this.states.forEach(state => {
            finalState.visible = state.visible ? state.visible : finalState.visible;
            finalState.position = state.position ? state.position : finalState.position;
            finalState.quaternion = state.quaternion ? state.quaternion : finalState.quaternion;
            finalState.scale = state.scale ? state.scale : finalState.scale;
            finalState.explosion = state.explosion ? state.explosion : finalState.explosion;
            finalState.glbAnimation = state.glbAnimation ? state.glbAnimation : finalState.glbAnimation;
            finalState.animationLoop = state.animationLoop ? state.animationLoop : finalState.animationLoop;
            finalState.highlight = state.highlight ? state.highlight : finalState.highlight;
            finalState.visibility = state.visibility ? state.visibility : finalState.visibility;
            finalState.transparency = state.transparency ? state.transparency : finalState.transparency;

            mergeVolumes(state);
            mergeBranches(state);
            mergeGroups(state);
        });

        function mergeVolumes(state) {
            if (state.hasOwnProperty('volumes')) {
                state.volumes.forEach(volume => {
                    let availableProperty = getPropertyByName('volumes', 'name', volume.name);
                    if (availableProperty) {
                        if (volume.position) availableProperty.position = volume.position;
                        if (volume.quaternion) availableProperty.quaternion = volume.quaternion;
                        if (volume.scale) availableProperty.scale = volume.scale;
                    } else {
                        finalState['volumes'].push({...volume});
                    }
                })
            }
        }

        function mergeBranches(state) {
            if (state.hasOwnProperty('branches')) {
                state.branches.forEach(branch => {
                    let availableProperty = getPropertyByName('branches', 'branchId', branch.branchId);
                    if (availableProperty) {
                        if (branch.position) availableProperty.position = branch.position;
                        if (branch.quaternion) availableProperty.quaternion = branch.quaternion;
                        if (branch.scale) availableProperty.scale = branch.scale;
                    } else {
                        finalState['branches'].push({...branch});
                    }
                })
            }
        }

        function mergeGroups(state) {
            if (state.hasOwnProperty('groups')) {
                state.groups.forEach(group => {
                    let availableProperty = getPropertyByName('groups', 'groupName', group.groupName);
                    if (availableProperty) {
                        if (group.position) availableProperty.position = group.position;
                        if (group.quaternion) availableProperty.quaternion = group.quaternion;
                        if (group.scale) availableProperty.scale = group.scale;
                    } else {
                        finalState['groups'].push({...group});
                    }
                })
            }
        }

        function getPropertyByName(propertyName, key, value) {
            if (!finalState[propertyName]) finalState[propertyName] = [];
            return finalState[propertyName].find(property => property[key] === value);
        }

        this.states = [];
        return finalState;
    }

    getDefaultState() {
        return {
            visible: false,
            position: [0, 0, 0],
            quaternion: [0, 0, 0, 1],
            scale: [1, 1, 1],
            explosion: 0.0,
            volumes: [],
            branches: [],
            glbAnimation: undefined,
            animationLoop: false,
            highlight: undefined,
            visibility: undefined,
            transparency: undefined,
            groups: [],
        };
    }

    getModelName(id) {
        try {
            const cacheFiles = wv.effectController.meetingInfo.data.cacheFiles;
            const cacheFile = cacheFiles.find(cacheFile => (cacheFile.id === id && !cacheFile.trashed));
            return cacheFile?.name;
        } catch (error) {
            console.error('Error getting model name from resource ID. Error: ' + error);
        }
    }

    compareStates(startStates, endStates) {
        let transitionStates = [];
        for (let state of startStates) {
            if (!endStates.find(endState => endState.id === state.id)) {
                transitionStates.push({
                    ...state,
                    visible: false
                });
            }
        }

        for (let state of endStates) {
            let equivalentStartState = startStates.find(startState => startState.id === state.id);

            if (!equivalentStartState) {
                transitionStates.push({...state});
            } else {
                transitionStates.push({
                    id: state.id,
                    visible: true,
                    explosion: state.explosion || "0",
                    ...compareModelState(equivalentStartState, state),
                    volumes: compareSubState(equivalentStartState.volumes || [], state.volumes || [], "id"),
                    branches: compareSubState(equivalentStartState.branches || [], state.branches || [], "branchId"),
                    groups: compareSubState(equivalentStartState.groups || [], state.groups || [], "groupId"),
                    highlight: state.highlight,
                    visibility: state.visibility,
                    transparency: state.transparency
                })
            }
        }

        return transitionStates;

        function compareModelState(startState, endState) {
            let startMatrix = new THREE.Matrix4().compose(
                new THREE.Vector3(...(startState.position || [0, 0, 0])),
                new THREE.Quaternion(...(startState.quaternion || [0, 0, 0, 1])),
                new THREE.Vector3(...(startState.scale || [1, 1, 1]))
            );
            let endMatrix = new THREE.Matrix4().compose(
                new THREE.Vector3(...(endState.position || [0, 0, 0])),
                new THREE.Quaternion(...(endState.quaternion || [0, 0, 0, 1])),
                new THREE.Vector3(...(endState.scale || [1, 1, 1]))
            );
            startMatrix.invert().multiply(endMatrix);
            let position = new THREE.Vector3();
            let quaternion = new THREE.Quaternion();
            let scale = new THREE.Vector3();
            startMatrix.decompose(position, quaternion, scale);
            position = position.toArray([]);
            quaternion = quaternion.toArray([]);
            scale = scale.toArray([]);
            return {position, quaternion, scale};
        }

        function compareSubState(startEntries, endEntries, idName) {
            let transitionEntries = [];

            for (let entry of startEntries) {
                if (!endEntries.find(endEntry => endEntry[idName] === entry[idName])) {
                    let matrix = new THREE.Matrix4().compose(
                        new THREE.Vector3(...(entry.position || [0, 0, 0])),
                        new THREE.Quaternion(...(entry.quaternion || [0, 0, 0, 1])),
                        new THREE.Vector3(...(entry.scale || [1, 1, 1]))
                    );

                    matrix.invert();
                    let position = new THREE.Vector3();
                    let quaternion = new THREE.Quaternion();
                    let scale = new THREE.Vector3();
                    matrix.decompose(position, quaternion, scale);
                    position = position.toArray([]);
                    quaternion = quaternion.toArray([]);
                    scale = scale.toArray([]);

                    transitionEntries.push({
                        ...entry,
                        position,
                        quaternion,
                        scale
                    });
                }
            }

            for (let entry of endEntries) {
                let equivalentStartEntry = startEntries.find(startEntry => startEntry[idName] === entry[idName]);

                if (!equivalentStartEntry) {
                    transitionEntries.push({...entry});
                } else {
                    let startMatrix = new THREE.Matrix4().compose(
                        new THREE.Vector3(...(equivalentStartEntry.position || [0, 0, 0])),
                        new THREE.Quaternion(...(equivalentStartEntry.quaternion || [0, 0, 0, 1])),
                        new THREE.Vector3(...(equivalentStartEntry.scale || [1, 1, 1]))
                    );
                    let endMatrix = new THREE.Matrix4().compose(
                        new THREE.Vector3(...(entry.position || [0, 0, 0])),
                        new THREE.Quaternion(...(entry.quaternion || [0, 0, 0, 1])),
                        new THREE.Vector3(...(entry.scale || [1, 1, 1]))
                    );
                    startMatrix.invert().multiply(endMatrix);
                    let position = new THREE.Vector3();
                    let quaternion = new THREE.Quaternion();
                    let scale = new THREE.Vector3();
                    startMatrix.decompose(position, quaternion, scale);
                    position = position.toArray([]);
                    quaternion = quaternion.toArray([]);
                    scale = scale.toArray([]);

                    transitionEntries.push({
                        ...entry,
                        position,
                        quaternion,
                        scale
                    })
                }
            }

            return transitionEntries;
        }
    }

    decompose(state, appendState) {
        const isDataAvailable = (action) => {
            if (action.hasOwnProperty('models') && action.models.length > 0) return true;
            else if (action.hasOwnProperty('volumes') && action.volumes.length > 0) return true;
            else if (action.hasOwnProperty('branches') && action.branches.length > 0) return true;
            else return !!(action.hasOwnProperty('groups') && action.groups.length > 0);
        };

        let actions = [];

        for (let entry of state) {
            actions.push({
                    startTime: 0,
                    actionId: generateUUID(),
                    type: "LoadState",
                    appendState: appendState,
                    loadViewpoint: false,
                    patchState: false,
                    states: [{
                        resourceStates: [
                            {
                                id: entry.id,
                                explosion: entry.explosion,
                                visible: entry.visible,
                                animationLoop: entry.animationLoop
                            }
                        ]
                    }]
                },
                {
                    startTime: 0,
                    type: "MoveModel",
                    transitionValues: appendState,
                    model: {id: entry.id, name: entry.name},
                    animateMovement: true,
                    animateTime: 1000,
                    position: entry.position,
                    quaternion: entry.quaternion,
                    scale: entry.scale
                });

            if (entry.hasOwnProperty('volumes')) {
                for (let volume of entry.volumes) {
                    actions.push({
                        startTime: 0,
                        type: "MoveVolume",
                        transitionValues: appendState,
                        model: {id: entry.model.id, name: entry.model.name},
                        volume: {id: volume.id, name: volume.name},
                        animateMovement: true,
                        animateTime: 1000,
                        position: volume.position,
                        quaternion: volume.quaternion,
                        scale: volume.scale
                    })
                }
            }

            if (entry.hasOwnProperty('branches')) {
                for (let branch of entry.branches) {
                    actions.push({
                        startTime: 0,
                        type: "MoveBranch",
                        transitionValues: appendState,
                        model: {id: entry.model.id, name: entry.model.name},
                        branchId: branch.branchId,
                        animateMovement: true,
                        animateTime: 1000,
                        position: branch.position,
                        quaternion: branch.quaternion,
                        scale: branch.scale
                    })
                }
            }

            if (entry.hasOwnProperty('groups')) {
                for (let group of entry.groups) {
                    actions.push({
                        startTime: 0,
                        type: "MoveGroup",
                        transitionValues: appendState,
                        model: {id: entry.model.id, name: entry.model.name},
                        groupName: group.groupName,
                        animateMovement: true,
                        animateTime: 1000,
                        position: group.position,
                        quaternion: group.quaternion,
                        scale: group.scale
                    })
                }
            }

            if (entry.highlight) {
                let enableAction = {
                    startTime: 0,
                    type: "Highlight",
                    enabled: true,
                    showTooltip: entry.highlight.showTooltip,
                    models: [],
                    volumes: [],
                    branches: [],
                    groups: []
                };

                let disableAction = {
                    startTime: 0,
                    type: "Highlight",
                    enabled: false,
                    showTooltip: entry.highlight.showTooltip,
                    models: [],
                    volumes: [],
                    branches: [],
                    groups: []
                };

                if (entry.highlight.models?.length) {
                    const model = entry.highlight.models[0];
                    if (entry.highlight.enabled) {
                        enableAction.models.push({id: model.id, name: model.name});
                    } else {
                        disableAction.models.push({id: model.id, name: model.name});
                    }
                }

                if (entry.highlight.volumes) {
                    for (let volume of entry.highlight.volumes) {
                        if (volume.isHighlighted) {
                            enableAction.volumes.push({id: volume.id, name: volume.name});
                        } else {
                            disableAction.volumes.push({id: volume.id, name: volume.name});
                        }
                    }
                }

                for (let branch of entry.highlight.branches) {
                    if (branch.isHighlighted) {
                        enableAction.branches.push({id: branch.id, name: branch.name});
                    } else {
                        disableAction.branches.push({id: branch.id, name: branch.name});
                    }
                }

                for (let group of entry.highlight.groups) {
                    if (group.isHighlighted) {
                        enableAction.groups.push({id: group.id, name: group.name});
                    } else {
                        disableAction.groups.push({id: group.id, name: group.name});
                    }
                }

                if (isDataAvailable(disableAction)) {
                    actions.push(disableAction);
                }
                if (isDataAvailable(enableAction)) {
                    actions.push(enableAction);
                }
            }

            if (entry.visibility) {
                let enableAction = {
                    startTime: 0,
                    type: "Visibility",
                    enabled: true,
                    models: [],
                    volumes: [],
                    branches: [],
                    groups: []
                };

                let disableAction = {
                    startTime: 0,
                    type: "Visibility",
                    enabled: false,
                    models: [],
                    volumes: [],
                    branches: [],
                    groups: []
                };

                if (entry.visibility.models?.length) {
                    const model = entry.highlight.models[0];

                    if (entry.visibility.enabled) {
                        enableAction.models.push({id: model.id, name: model.name});
                    } else {
                        disableAction.models.push({id: model.id, name: model.name});
                    }
                }

                if (entry.visibility.volumes) {
                    for (let volume of entry.visibility.volumes) {
                        if (volume.isVisible) {
                            enableAction.volumes.push({id: volume.id, name: volume.name});
                        } else {
                            disableAction.volumes.push({id: volume.id, name: volume.name});
                        }
                    }
                }

                for (let branch of entry.visibility.branches) {
                    if (branch.isVisible) {
                        enableAction.branches.push({id: branch.id, name: branch.name});
                    } else {
                        disableAction.branches.push({id: branch.id, name: branch.name});
                    }
                }

                for (let group of entry.visibility.groups) {
                    if (group.isVisible) {
                        enableAction.groups.push({id: group.id, name: group.name});
                    } else {
                        disableAction.groups.push({id: group.id, name: group.name});
                    }
                }

                if (isDataAvailable(disableAction)) {
                    actions.push(disableAction);
                }
                if (isDataAvailable(enableAction)) {
                    actions.push(enableAction);
                }
            }

            if (entry.transparency) {
                let enableAction = {
                    startTime: 0,
                    type: "Transparency",
                    enabled: true,
                    models: [],
                    volumes: []
                };

                let disableAction = {
                    startTime: 0,
                    type: "Transparency",
                    enabled: false,
                    models: [],
                    volumes: []
                };

                if (entry.transparency.models?.length) {
                    const model = entry.highlight.models[0];

                    if (entry.transparency.enabled) {
                        enableAction.models.push({id: model.id, name: model.name});
                    } else {
                        disableAction.models.push({id: model.id, name: model.name});
                    }
                }

                if (entry.transparency.volumes) {
                    for (let volume of entry.transparency.volumes) {
                        if (entry.transparency.enabled) {
                            enableAction.volumes.push({id: volume.id, name: volume.name});
                        }
                    }

                    for (let volume of entry.transparency.volumes) {
                        if (!entry.transparency.enabled) {
                            disableAction.volumes.push({id: volume.id, name: volume.name});
                        }
                    }
                }

                if (isDataAvailable(disableAction)) {
                    actions.push(disableAction);
                }
                if (isDataAvailable(enableAction)) {
                    actions.push(enableAction);
                }
            }
        }

        return actions;
    }

}

export default Model;
